import {
  Card,
  Grid,
  CardContent,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
import { Bar } from "react-chartjs-2";
import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { useTranslation } from 'react-i18next';
import { typoBase, smallBold, thinBold } from "../styles/typography";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { getTimesheetsByCaseId } from "../redux/actions/timesheetActions";
import { getSalesPaymentsByCaseId } from "../redux/actions/salesPaymentActions";
import { getSalesDocumentsByCaseId } from "../redux/actions/salesDocumentActions";

// Register the components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

const FinanceCard = () => {
  const { t } = useTranslation('common');
  const { timesheets, loading, error } = useSelector(
    (state) => state.timesheetList
  );
  const { salesPayments } = useSelector((state) => state.salesPaymentList);
  const { salesDocuments = [] } = useSelector((state) => state.salesDocumentList);
  const { id } = useParams(); // Assuming the case ID is passed as a URL parameter
  const dispatch = useDispatch();

  // State for filtered data
  const [filteredTimesheets, setFilteredTimesheets] = useState([]);
  const [filteredSalesPayments, setFilteredSalesPayments] = useState([]);
  const [filteredSalesDocuments, setFilteredSalesDocuments] = useState([]);

  useEffect(() => {
    dispatch(getTimesheetsByCaseId(id)); // Fetch timesheets based on the case ID
    dispatch(getSalesPaymentsByCaseId(id));
    dispatch(getSalesDocumentsByCaseId(id));
  }, [dispatch, id]);

  // Filter timesheets by case ID
  useEffect(() => {
    if (timesheets) {
      const filtered = timesheets.filter((timesheet) =>
        id === timesheet.fileCase?._id
      );
      setFilteredTimesheets(filtered);
    }
  }, [timesheets, id]);

  // Filter sales payments by case ID
  useEffect(() => {
    if (salesPayments) {
      const filtered = salesPayments.filter((salesPayment) =>
        id === salesPayment.case?._id
      );
      setFilteredSalesPayments(filtered);
    }
  }, [salesPayments, id]);

  // Filter sales documents by case ID
  useEffect(() => {
    if (salesDocuments) {
      const filtered = salesDocuments.filter((salesDocument) =>
        id === salesDocument.case?._id
      );
      setFilteredSalesDocuments(filtered);
    }
  }, [salesDocuments, id]);

  const convertToDecimalHours = (time) => {
    const [hours, minutes, seconds] = time.split(":").map(Number);
    return (hours + minutes / 60 + seconds / 3600).toFixed(2);
  };

  // Format decimal hours to "Xh Ym" format
  const formatHoursAndMinutes = (decimalHours) => {
    const totalMinutes = Math.round(decimalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours === 0 && minutes === 0) {
      return `0h 0m`;
    } else if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  };

  // Calculate hours based on timesheet status (isBilled) - only billable items
  const calculateValues = (type) => {
    let billed = 0;
    let notBilled = 0;

    if (filteredTimesheets && Array.isArray(filteredTimesheets)) {
      filteredTimesheets.forEach((timesheet) => {
        if (timesheet.billingItems && Array.isArray(timesheet.billingItems)) {
          timesheet.billingItems.forEach((item) => {
            // Only count billable items - match type with underscore
            if (item.type === type && item.billable === true) {
              const value = parseFloat(convertToDecimalHours(item.quantity));
              if (timesheet.isBilled) {
                billed += value;
              } else {
                notBilled += value;
              }
            }
          });
        }
      });
    }

    return { billed, notBilled };
  };

  // Calculate Total Billed Amount from all invoices (sales documents)
  const calculateTotalBilledAmount = () => {
    let totalBilledAmount = 0;
    
    if (filteredSalesDocuments && Array.isArray(filteredSalesDocuments)) {
      filteredSalesDocuments.forEach((doc) => {
        if (doc.type === "invoice" || doc.type === "provision note") {
          totalBilledAmount += parseFloat(doc.total || 0);
        }
      });
    }

    return totalBilledAmount;
  };

  // Calculate Total Not Billed Amount from timesheets with status not billed
  const calculateTotalNotBilledAmount = () => {
    let totalNotBilledAmount = 0;
    
    if (filteredTimesheets && Array.isArray(filteredTimesheets)) {
      filteredTimesheets.forEach((timesheet) => {
        if (!timesheet.isBilled) {
          totalNotBilledAmount += parseFloat(timesheet.totalAmount || 0);
        }
      });
    }

    return totalNotBilledAmount;
  };

  // Calculate Amount to be Paid = (Invoices + Provision Notes) - Credits - Payments
  const calculateAmountToBePaid = () => {
    let totalFromSalesDocuments = 0;
    let totalCredits = 0;
    let totalPayments = 0;
    
    // Calculate total from sales documents (invoices + provision notes)
    if (filteredSalesDocuments && Array.isArray(filteredSalesDocuments)) {
      filteredSalesDocuments.forEach((doc) => {
        if (doc.type === "invoice" || doc.type === "provision note") {
          totalFromSalesDocuments += parseFloat(doc.total || 0);
        } else if (doc.type === "credit") {
          totalCredits += parseFloat(doc.total || 0);
        }
      });
    }

    // Calculate total payments
    if (filteredSalesPayments && Array.isArray(filteredSalesPayments)) {
      filteredSalesPayments.forEach((payment) => {
        totalPayments += parseFloat(payment.amount || 0);
      });
    }

    const amountToBePaid = totalFromSalesDocuments  - totalPayments;

    return { amountToBePaid, totalPayments, totalCredits };
  };

  const fees = calculateValues("working_time");
  const travel = calculateValues("travel_time");
  const costs = calculateValues("kilometers");

  // Combine travel time into fees for hours calculation
  fees.billed += parseFloat(travel.billed);
  fees.notBilled += parseFloat(travel.notBilled);

  const { amountToBePaid, totalPayments, totalCredits } = calculateAmountToBePaid();
  const totalBilledAmount = calculateTotalBilledAmount();
  const totalNotBilledAmount = calculateTotalNotBilledAmount();

  // Calculate amounts for chart (fees and costs by billing status) - only billable items
  const calculateAmountsForChart = () => {
    let feesBilledAmount = 0;
    let feesNotBilledAmount = 0;
    let costsBilledAmount = 0;
    let costsNotBilledAmount = 0;

    if (filteredTimesheets && Array.isArray(filteredTimesheets)) {
      filteredTimesheets.forEach((timesheet) => {
        if (timesheet.billingItems && Array.isArray(timesheet.billingItems)) {
          timesheet.billingItems.forEach((item) => {
            // Only count billable items
            if (item.billable === true) {
              // Calculate amount: hours * rate
              const hours = parseFloat(convertToDecimalHours(item.quantity));
              const rate = parseFloat(item.rate || 0);
              const amount = hours * rate;
              
              if (item.type === "working_time" || item.type === "travel_time") {
                if (timesheet.isBilled) {
                  feesBilledAmount += amount;
                } else {
                  feesNotBilledAmount += amount;
                }
              } else if (item.type === "kilometers") {
                if (timesheet.isBilled) {
                  costsBilledAmount += amount;
                } else {
                  costsNotBilledAmount += amount;
                }
              }
            }
          });
        }
      });
    }

    return { feesBilledAmount, feesNotBilledAmount, costsBilledAmount, costsNotBilledAmount };
  };

  const { feesBilledAmount, feesNotBilledAmount, costsBilledAmount, costsNotBilledAmount } = calculateAmountsForChart();

  const data = {
    labels: [t('fees'), t('costs')],
    datasets: [
      {
        label: t('not_billed_amount'),
        data: [feesNotBilledAmount, costsNotBilledAmount],
        backgroundColor: "#efab40",
        barThickness: 10, // Adjust the bar width
      },
      {
        label: t('billed_amount'),
        data: [feesBilledAmount, costsBilledAmount],
        backgroundColor: "#940c72",
        barThickness: 10, // Adjust the bar width
      },
    ],
  };
  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false, // Disable the default tooltip
        external: (context) => {
          // Tooltip Element
          let tooltipEl = document.getElementById("chartjs-tooltip");

          // Create element on first render
          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.innerHTML =
              '<div class="tooltip-arrow"></div><table></table>';
            document.body.appendChild(tooltipEl);
          }

          // Hide if no tooltip
          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = 0;
            return;
          }

          // Set caret Position
          tooltipEl.classList.remove("above", "below", "no-transform");
          if (tooltipModel.yAlign) {
            tooltipEl.classList.add(tooltipModel.yAlign);
          } else {
            tooltipEl.classList.add("no-transform");
          }

          function getBody(bodyItem) {
            return bodyItem.lines;
          }

          // Set Text
          if (tooltipModel.body) {
            const index = context.tooltip.dataPoints[0].dataIndex;
            const billedAmount = parseInt(data.datasets[1].data[index]);
            const notBilledAmount = parseInt(data.datasets[0].data[index]);
            const totalAmount = billedAmount + notBilledAmount;

            let innerHtml = "<thead></thead><tbody>";
            innerHtml += `
              <tr>
                <td>
                  <span style="background:#940c72;width:10px;height:10px;border-radius:50%;display:inline-block;margin-right:5px;"></span>
                  ${t('billed_amount')}: € ${billedAmount}
                </td>
              </tr>
              <tr>
                <td>
                  <span style="background:#efab40;width:10px;height:10px;border-radius:50%;display:inline-block;margin-right:5px;"></span>
                  ${t('not_billed_amount')}: € ${notBilledAmount}
                </td>
              </tr>
              <tr>
                <td>${t('total_amount')}: € ${totalAmount}</td>
              </tr>
            `;
            innerHtml += "</tbody>";

            const tableRoot = tooltipEl.querySelector("table");
            tableRoot.innerHTML = innerHtml;
          }

          // `this` will be the overall tooltip
          const position = context.chart.canvas.getBoundingClientRect();

          // Display, position, and set styles for font
          tooltipEl.style.opacity = 1;
          tooltipEl.style.position = "absolute";
          tooltipEl.style.left =
            position.left +
            window.pageXOffset +
            tooltipModel.caretX +
            100 +
            "px"; // Move to the right
          tooltipEl.style.top =
            position.top + window.pageYOffset + tooltipModel.caretY - 20 + "px"; // Center vertically
          tooltipEl.style.fontFamily = tooltipModel.options.bodyFont.family;
          tooltipEl.style.fontSize = tooltipModel.options.bodyFont.size + "px";
          tooltipEl.style.fontStyle = tooltipModel.options.bodyFont.style;
          tooltipEl.style.padding = "10px"; // Add padding
          tooltipEl.style.pointerEvents = "none";
        },
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        ticks: {
          callback: (value) => `€ ${value}`,
        },
      },
    },
  };

  // Add custom CSS for tooltip
  const tooltipStyle = `
    #chartjs-tooltip {
      opacity: 0;
      position: absolute;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      border-radius: 3px;
      pointer-events: none;
      transform: translate(-50%, 0);
      transition: all .1s ease;
      padding: 10px;
    }
  
    #chartjs-tooltip .tooltip-arrow {
      width: 0;
      height: 0;
      border-left: 5px solid rgba(0, 0, 0, 0.7);
      border-top: 5px solid transparent;
      border-bottom: 5px solid transparent;
      position: absolute;
      left: -5px;
      top: 50%;
      transform: translateY(-50%);
      transform: rotate(180deg);
    }

  
    #chartjs-tooltip table {
      margin: 0;
    }
  `;

  const styleElement = document.createElement("style");
  styleElement.innerHTML = tooltipStyle;
  document.head.appendChild(styleElement);

  const totalBilledHours = fees.billed;
  const totalNotBilledHours = fees.notBilled;

  const totalHours = totalBilledHours + totalNotBilledHours;

  return (
    <Card sx={{ boxShadow: "none", border: "none" }}>
      <CardContent>
        {/* <Typography variant="h6" sx={thinBold}>
          Finance Overview
        </Typography> */}
        <Box mt={3} display="flex" justifyContent="center" gap={2}>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: "7px",
                height: "7px",
                backgroundColor: "#940c72",
                borderRadius: "50%",
                marginRight: "8px",
              }}
            />
            <Typography
              style={{
                color: "rgb(117, 117, 117)",
                display: "block",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "10.5px",
                fontWeight: 400,
                height: "13.5px",
                lineHeight: "13.5px",
              }}
            >
              {t('billingStatus.billed')}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <Box
              sx={{
                width: "7px",
                height: "7px",
                backgroundColor: "#efab40",
                borderRadius: "50%",
                marginRight: "8px",
              }}
            />
            <Typography
              style={{
                color: "rgb(117, 117, 117)",
                display: "block",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "10.5px",
                fontWeight: 400,
                height: "13.5px",
                lineHeight: "13.5px",
              }}
            >
              {t('billingStatus.notBilled')}
            </Typography>
          </Box>
        </Box>
        <Box mt={3}>
          <Bar data={data} options={options} />
        </Box>
        <Box mt={3} display="flex" justifyContent="space-between">
          <Typography
            style={{
              color: "rgb(117, 117, 117)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "9px",
              fontWeight: 500,
              height: "13.5px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            {t('labels.timeSpent')}
          </Typography>
          <Typography
            style={{
              color: "rgb(35, 35, 35)",
              display: "block",
              fontFamily:
                '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
              fontSize: "9px",
              fontWeight: 500,
              height: "13.5px",
              lineHeight: "13.5px",
            }}
          >
            {formatHoursAndMinutes(totalHours)}
          </Typography>
        </Box>
        <Tooltip
          title={
            <Box>
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: "10px",
                    height: "10px",
                    backgroundColor: "#940c72",
                    borderRadius: "50%",
                    marginRight: "8px",
                  }}
                />
                <Typography variant="body2" sx={typoBase}>
                  {t('billed_hours')}: {formatHoursAndMinutes(totalBilledHours)}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: "10px",
                    height: "10px",
                    backgroundColor: "#efab40",
                    borderRadius: "50%",
                    marginRight: "8px",
                  }}
                />
                <Typography variant="body2" sx={typoBase}>
                  {t('not_billed_hours')}: {formatHoursAndMinutes(totalNotBilledHours)}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Typography variant="body2" sx={typoBase}>
                  {t('total_hours')}: {formatHoursAndMinutes(totalHours)}
                </Typography>
              </Box>
            </Box>
          }
        >
          <Box mt={1} sx={{ width: "100%", height: "6px", display: "flex",  }}>
            <Box
              sx={{
                width: `${totalHours > 0 ? (totalBilledHours / totalHours) * 100 : 0}%`,
                backgroundColor: "#940c72",
              }}
            />
            <Box
              sx={{
                width: `${totalHours > 0 ? (totalNotBilledHours / totalHours) * 100 : 0}%`,
                backgroundColor: "#efab40",
              }}
            />
          </Box>
        </Tooltip>
        <Grid container mt={3} >
        <Grid
            item
            xs={6}
            display="flex"
            flexDirection="column"
            alignItems="center"
            paddingTop={2}
          >
            <Typography
              sx={{
                color: "rgb(35, 35, 35)",
                display: "block",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "10.5px",
                fontWeight: 600,
                height: "15.75px",
                lineHeight: "15.75px",
              }}
            >
              {/* € {totalPayments + amountAfterPayments} */}
              €  {totalNotBilledAmount.toFixed(2)}
            </Typography>
            <Typography
              variant="body1"
              style={{
                color: "rgb(117, 117, 117)",
                display: "block",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "9px",
                fontWeight: 400,
                height: "13.5px",
                lineHeight: "13.5px",
              }}
            >
              {t('labels.totalNotBilledAmount')}
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            display="flex"
            flexDirection="column"
            alignItems="center"
            paddingTop={2}
          >
            <Typography
              sx={{
                color: "rgb(35, 35, 35)",
                display: "block",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "10.5px",
                fontWeight: 600,
                height: "15.75px",
                lineHeight: "15.75px",
              }}
            >
              €  {totalBilledAmount.toFixed(2)}
            </Typography>
            <Typography
              style={{
                color: "rgb(117, 117, 117)",
                display: "block",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "9px",
                fontWeight: 400,
                height: "13.5px",
                lineHeight: "13.5px",
              }}
            >
              {t('labels.totalBilledAmount')}
            </Typography>
          </Grid>
          
          <Grid
            item
            xs={6}
            display="flex"
            flexDirection="column"
            alignItems="center"
            paddingTop={2}
          >
            <Typography
              sx={{
                color: "rgb(35, 35, 35)",
                display: "block",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "10.5px",
                fontWeight: 600,
                height: "15.75px",
                lineHeight: "15.75px",
              }}
            >
              €  {amountToBePaid.toFixed(2)}
            </Typography>
            <Typography
              style={{
                color: "rgb(117, 117, 117)",
                display: "block",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "9px",
                fontWeight: 400,
                height: "13.5px",
                lineHeight: "13.5px",
              }}
            >
              {t('labels.amountToBePaid')}
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            display="flex"
            flexDirection="column"
            alignItems="center"
            paddingTop={2}
          >
            <Typography
              sx={{
                color: "rgb(35, 35, 35)",
                display: "block",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "10.5px",
                fontWeight: 600,
                height: "15.75px",
                lineHeight: "15.75px",
              }}
            >
              €  {totalPayments.toFixed(2)}
            </Typography>
            <Typography
              style={{
                color: "rgb(117, 117, 117)",
                display: "block",
                fontFamily:
                  '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                fontSize: "9px",
                fontWeight: 400,
                height: "13.5px",
                lineHeight: "13.5px",
              }}
            >
              {t('payments')}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default FinanceCard;